/**
 * Stage 19 · Customer Service Governance, Monthly Review & QBR Centre.
 *
 * Always-on governance replacing manual PowerPoint QBRs.
 * Every number on the scorecard comes from verified platform data.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Gauge, ShieldAlert, ClipboardList, Gavel, Presentation,
  CalendarCheck2, Plus, Lock, RefreshCw, ArrowUpRight,
  TrendingUp, TrendingDown, Building2, Sparkles,
} from "lucide-react";

const TABS = [
  { key: "meeting",  label: "Meeting mode", icon: Presentation },
  { key: "reviews",  label: "Reviews",      icon: CalendarCheck2 },
  { key: "scorecard",label: "SLA scorecard",icon: Gauge },
  { key: "risks",    label: "Risks",        icon: ShieldAlert },
  { key: "actions",  label: "Actions",      icon: ClipboardList },
  { key: "decisions",label: "Decisions",    icon: Gavel },
];

export default function GovernanceCentre() {
  const [tab, setTab] = useState("meeting");
  const [masters, setMasters] = useState([]);
  const [mc, setMc] = useState("");
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setMe(data);
      if (data?.master_company_id) setMc(data.master_company_id);
    }).catch(() => {});
    api.get("/master-companies").then(({ data }) => {
      const items = data.items || [];
      setMasters(items);
      setMc((current) => current || items[0]?.id || "");
    }).catch(() => {});
  }, []);

  const isSuntronix = me && !me.master_company_id;
  const activeCustomer = masters.find((m) => m.id === mc);

  return (
    <div className="max-w-6xl space-y-6" data-testid="governance-centre">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
          Customer Success · Stage 19
        </div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">
          Governance & QBR Centre
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every SLA, risk, action and decision — live from the platform. No decks. No slide edits.
          Monthly reviews and quarterly business reviews are generated from verified operational data.
        </p>
      </div>

      {isSuntronix && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Customer</div>
          <div className="relative">
            <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              className="input pl-8 min-w-[280px]"
              value={mc} onChange={(e) => setMc(e.target.value)}
              data-testid="governance-tenant-select">
              {masters.map((m) => (
                <option key={m.id} value={m.id}>{m.name}{m.is_demo ? " · DEMO" : ""}</option>
              ))}
            </select>
          </div>
          {activeCustomer && (
            <span className="text-[10px] font-mono text-slate-500">{activeCustomer.code}</span>
          )}
        </div>
      )}

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            data-testid={`gov-tab-${key}`}
            onClick={() => setTab(key)}
            className={
              "px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors " +
              (tab === key
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800")
            }>
            <Icon className="h-4 w-4" strokeWidth={1.5} /> {label}
          </button>
        ))}
      </nav>

      {mc ? (
        <div className="pt-1">
          {tab === "meeting"   && <MeetingMode mc={mc} isSuntronix={isSuntronix} />}
          {tab === "reviews"   && <ReviewsTab mc={mc} isSuntronix={isSuntronix} />}
          {tab === "scorecard" && <ScorecardTab mc={mc} />}
          {tab === "risks"     && <RisksTab mc={mc} isSuntronix={isSuntronix} />}
          {tab === "actions"   && <ActionsTab mc={mc} isSuntronix={isSuntronix} />}
          {tab === "decisions" && <DecisionsTab mc={mc} isSuntronix={isSuntronix} />}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md p-8 text-sm text-slate-500">
          Select a customer to open the governance pack.
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Meeting mode */
function MeetingMode({ mc }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(() =>
    api.get("/governance-reviews/meeting-mode", { params: { master_company_id: mc, days } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null)), [days, mc]);

  useEffect(() => { if (mc) load(); }, [mc, days, load]);

  if (!data) return <SkeletonBlock />;

  return (
    <div className="space-y-6" data-testid="gov-meeting-mode">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Period</div>
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="meeting-days">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last quarter</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last year</option>
        </select>
        <button onClick={load} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm inline-flex items-center gap-1" data-testid="meeting-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
        <span className="ml-auto text-[11px] text-slate-500">
          Live from platform data · generated {new Date(data.generated_at).toLocaleString()}
        </span>
      </div>

      <ScorecardCard scorecard={data.scorecard} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryTile label="Open risks"     value={data.scorecard?.governance?.open_risks}     tone={data.scorecard?.governance?.critical_risks ? "danger" : "neutral"} suffix={data.scorecard?.governance?.critical_risks ? `${data.scorecard.governance.critical_risks} critical` : null} />
        <SummaryTile label="Open actions"   value={data.scorecard?.governance?.open_actions}   tone={data.scorecard?.governance?.overdue_actions ? "warn" : "neutral"} suffix={data.scorecard?.governance?.overdue_actions ? `${data.scorecard.governance.overdue_actions} overdue` : null} />
        <SummaryTile label="Recent decisions" value={data.recent_decisions?.length ?? 0} />
      </div>

      <RegistrySection title="Top open risks" items={data.top_risks || []} render={(r) => (
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-900">{r.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{r.description || "—"}</div>
          </div>
          <span className={severityChip(r.severity)}>{r.severity}</span>
        </div>
      )} />

      <RegistrySection title="Open actions" items={data.open_actions || []} render={(a) => (
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-900">{a.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">Owner: {a.owner_side}{a.owner_email ? ` · ${a.owner_email}` : ""}</div>
          </div>
          <div className="text-xs tabular-nums text-slate-600">{a.due_date || "—"}</div>
        </div>
      )} />

      <RegistrySection title="Recent decisions" items={data.recent_decisions || []} render={(d) => (
        <div>
          <div className="text-sm font-medium text-slate-900">{d.decision}</div>
          <div className="text-xs text-slate-500 mt-0.5">{d.context || "—"} · Approved by {d.approved_by || "—"}</div>
        </div>
      )} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PublishedReviewCard label="Last MSR"  review={data.last_msr} />
        <PublishedReviewCard label="Last QBR"  review={data.last_qbr} />
      </div>
    </div>
  );
}

function PublishedReviewCard({ label, review }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      {review ? (
        <>
          <div className="text-sm font-mono text-slate-900 mt-1">{review.review_number}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {review.period_start?.slice(0,10)} → {review.period_end?.slice(0,10)}
          </div>
          {review.published_at && (
            <div className="text-[10px] text-emerald-700 mt-1 inline-flex items-center gap-1">
              <Lock className="h-3 w-3" /> Published {new Date(review.published_at).toLocaleDateString()}
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-slate-500 mt-1">Not yet published.</div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Reviews */
function ReviewsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [kind, setKind] = useState("");

  const load = useCallback(() =>
    api.get("/governance-reviews/reviews", { params: { master_company_id: mc, kind: kind || undefined } })
      .then(({ data }) => setItems(data.items || [])), [kind, mc]);

  useEffect(() => { if (mc) load(); setSelected(null); }, [mc, kind, load]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="gov-reviews-tab">
      <div className="md:col-span-1 space-y-3">
        <div className="flex items-center gap-2">
          <select className="input flex-1" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="reviews-kind-filter">
            <option value="">All reviews</option>
            <option value="msr">Monthly (MSR)</option>
            <option value="qbr">Quarterly (QBR)</option>
          </select>
          {isSuntronix && (
            <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1" data-testid="review-new">
              <Plus className="h-4 w-4" /> New
            </button>
          )}
        </div>

        {showForm && (
          <NewReviewForm mc={mc} onCancel={() => setShowForm(false)} onCreated={(r) => { setShowForm(false); load(); setSelected(r); }} />
        )}

        <div className="bg-white border border-slate-200 rounded-md divide-y">
          {items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No reviews scheduled yet.</div>
          ) : items.map((r) => (
            <button key={r.id} onClick={() => setSelected(r)} data-testid={`review-row-${r.review_number}`}
              className={"w-full text-left p-3 hover:bg-slate-50 " + (selected?.id === r.id ? "bg-slate-50" : "")}>
              <div className="text-sm font-mono text-slate-900">{r.review_number}</div>
              <div className="text-xs text-slate-600 mt-0.5">{r.title}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={kindChip(r.kind)}>{r.kind?.toUpperCase()}</span>
                <span className={statusChip(r.status)}>{r.status?.replace("_", " ")}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-1 tabular-nums">
                {r.period_start?.slice(0,10)} → {r.period_end?.slice(0,10)}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        {selected
          ? <ReviewDetail reviewId={selected.id} isSuntronix={isSuntronix} onChanged={load} />
          : <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-md p-8">Select a review on the left.</div>}
      </div>
    </div>
  );
}

function NewReviewForm({ mc, onCancel, onCreated }) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [f, setF] = useState({ kind: "msr", period_start: first, period_end: last, title: "" });

  const save = async () => {
    try {
      const { data } = await api.post("/governance-reviews/reviews", { master_company_id: mc, ...f });
      toast.success(`${data.review_number} created`);
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="review-form">
      <div className="grid grid-cols-2 gap-2">
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="review-kind">
            <option value="msr">Monthly (MSR)</option>
            <option value="qbr">Quarterly (QBR)</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Title</div>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Optional" data-testid="review-title" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Period start</div>
          <input type="date" className="input" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })} data-testid="review-start" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Period end</div>
          <input type="date" className="input" value={f.period_end} onChange={(e) => setF({ ...f, period_end: e.target.value })} data-testid="review-end" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="review-save">Create</button>
      </div>
    </div>
  );
}

function ReviewDetail({ reviewId, isSuntronix, onChanged }) {
  const [pack, setPack] = useState(null);
  const load = useCallback(() => api.get(`/governance-reviews/reviews/${reviewId}`).then(({ data }) => setPack(data)), [reviewId]);
  useEffect(() => { load(); }, [load, reviewId]);
  if (!pack) return <SkeletonBlock />;

  const canPublish = isSuntronix && pack.status !== "published";
  const canEdit = isSuntronix && pack.status !== "published";

  const publish = async () => {
    if (!window.confirm("Publish this review? The scorecard will be frozen for audit.")) return;
    try {
      await api.post(`/governance-reviews/reviews/${reviewId}/publish`);
      toast.success("Review published — scorecard frozen.");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/governance-reviews/reviews/${reviewId}`, { status });
      toast.success("Updated");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="review-detail">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-start gap-3">
          <div>
            <div className="text-sm font-mono text-slate-900">{pack.review_number}</div>
            <div className="text-lg font-heading font-semibold text-slate-900 mt-0.5">{pack.title}</div>
            <div className="text-xs text-slate-500 mt-1 tabular-nums">
              {pack.period_start?.slice(0,10)} → {pack.period_end?.slice(0,10)}
              {" · "}<span className={statusChip(pack.status)}>{pack.status}</span>
              {" · "}Data source: <span className="text-slate-700">{pack.scorecard_source}</span>
              {pack.status === "published" && (
                <span className="ml-2 text-emerald-700 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Immutable snapshot</span>
              )}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {canEdit && pack.status === "scheduled" && (
              <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={() => setStatus("in_progress")} data-testid="review-start">
                Start meeting
              </button>
            )}
            {canEdit && pack.status === "in_progress" && (
              <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={() => setStatus("held")} data-testid="review-hold">
                Mark held
              </button>
            )}
            {canPublish && (
              <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" onClick={publish} data-testid="review-publish">
                <Lock className="h-4 w-4" /> Publish
              </button>
            )}
          </div>
        </div>
      </div>

      <ScorecardCard scorecard={pack.scorecard} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RegistrySection title="Risks" items={pack.risks?.slice(0, 8) || []} render={(r) => (
          <div>
            <div className="text-sm text-slate-900 font-medium flex items-center gap-2">
              {r.title} <span className={severityChip(r.severity)}>{r.severity}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Status: {r.status}</div>
          </div>
        )} />
        <RegistrySection title="Actions" items={pack.actions?.slice(0, 8) || []} render={(a) => (
          <div>
            <div className="text-sm text-slate-900 font-medium">{a.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">Owner: {a.owner_side} · Due {a.due_date || "—"} · {a.status}</div>
          </div>
        )} />
      </div>

      <RegistrySection title="Decisions" items={pack.decisions?.slice(0, 8) || []} render={(d) => (
        <div>
          <div className="text-sm text-slate-900 font-medium">{d.decision}</div>
          <div className="text-xs text-slate-500 mt-0.5">{d.context || "—"} · Approved by {d.approved_by || "—"}</div>
        </div>
      )} />
    </div>
  );
}

/* ---------------------------------------------------------------- Scorecard */
function ScorecardTab({ mc }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    if (!mc) return;
    const end = new Date().toISOString();
    const start = new Date(Date.now() - days * 86400_000).toISOString();
    api.get("/governance-reviews/scorecard", { params: { master_company_id: mc, period_start: start, period_end: end } })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, [mc, days]);

  return (
    <div className="space-y-4" data-testid="gov-scorecard-tab">
      <div className="flex items-center gap-3">
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="scorecard-days">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last quarter</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last year</option>
        </select>
      </div>
      {data ? <ScorecardCard scorecard={data} /> : <SkeletonBlock />}
    </div>
  );
}

function ScorecardCard({ scorecard }) {
  if (!scorecard) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-md" data-testid="scorecard-card">
      <div className="p-4 border-b border-slate-200 flex items-center gap-2">
        <Gauge className="h-4 w-4 text-slate-500" />
        <h2 className="text-lg font-heading font-semibold text-slate-900">SLA scorecard</h2>
        <span className="ml-auto text-[10px] text-slate-500 tabular-nums">
          Period {scorecard.period_start?.slice(0,10)} → {scorecard.period_end?.slice(0,10)}
        </span>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {(scorecard.sla || []).map((m) => (
          <div key={m.key} className="rounded-md border border-slate-200 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{m.label}</div>
            <div className="mt-1 text-2xl font-heading font-semibold tabular-nums text-slate-900">
              {m.value == null ? "—" : m.value}
              {m.value != null && <span className="text-sm font-normal opacity-70"> {m.unit}</span>}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">{m.lower_is_better ? "Lower is better" : "Higher is better"}</div>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <MetricRow label="Orders placed"    value={scorecard.operational?.orders_placed} />
        <MetricRow label="Orders delivered" value={scorecard.operational?.orders_delivered} />
        <MetricRow label="Shipments"        value={scorecard.operational?.shipments} />
        <MetricRow label="Pickups"          value={scorecard.operational?.pickups} />
        <MetricRow label="Requests"         value={scorecard.operational?.requests_created} />
      </div>
      <div className="border-t border-slate-200 p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <MetricRow label="Invoiced (INR)"  value={fmt(scorecard.financial?.invoiced_total_inr)} />
        <MetricRow label="Paid (INR)"      value={fmt(scorecard.financial?.invoiced_paid_inr)} tone="ok" />
        <MetricRow label="Outstanding"     value={fmt((scorecard.financial?.invoiced_total_inr || 0) - (scorecard.financial?.invoiced_paid_inr || 0))} tone="warn" />
      </div>
    </section>
  );
}

function MetricRow({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"text-lg font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- Risks */
function RisksTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => api.get("/governance-reviews/risks", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { if (mc) load(); }, [load, mc]);

  return (
    <div className="space-y-4" data-testid="gov-risks-tab">
      <div className="flex justify-end">
        {isSuntronix && (
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="risk-new">
            <Plus className="h-4 w-4" /> New risk
          </button>
        )}
      </div>
      {showForm && <RiskForm mc={mc} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <RegistryTable
        columns={["Title", "Severity", "Owner", "Status", "Due"]}
        rows={items}
        renderRow={(r) => [r.title, <span key="sev" className={severityChip(r.severity)}>{r.severity}</span>, r.owner || "—", <StatusEditor key="st" kind="risk" item={r} onChanged={load} disabled={!isSuntronix} />, r.due_date || "—"]}
        testId="risks-table"
      />
    </div>
  );
}

function RiskForm({ mc, onCancel, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", severity: "medium", owner: "", mitigation: "", due_date: "" });
  const save = async () => {
    try {
      await api.post("/governance-reviews/risks", { master_company_id: mc, ...f });
      toast.success("Risk logged");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="risk-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Title</div>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="risk-title" />
        </label>
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <textarea className="input min-h-[70px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="risk-description" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Severity</div>
          <select className="input" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid="risk-severity">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Owner</div>
          <input className="input" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="risk-owner" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Due date</div>
          <input type="date" className="input" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="risk-due" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Mitigation</div>
          <input className="input" value={f.mitigation} onChange={(e) => setF({ ...f, mitigation: e.target.value })} data-testid="risk-mitigation" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="risk-save">Log risk</button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Actions */
function ActionsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/governance-reviews/actions", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { if (mc) load(); }, [load, mc]);

  return (
    <div className="space-y-4" data-testid="gov-actions-tab">
      <div className="flex justify-end">
        {isSuntronix && (
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="action-new">
            <Plus className="h-4 w-4" /> New action
          </button>
        )}
      </div>
      {showForm && <ActionForm mc={mc} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <RegistryTable
        columns={["Title", "Owner", "Due", "Status"]}
        rows={items}
        renderRow={(a) => [a.title, `${a.owner_side}${a.owner_email ? ` · ${a.owner_email}` : ""}`, a.due_date || "—", <StatusEditor key="st" kind="action" item={a} onChanged={load} disabled={!isSuntronix} />]}
        testId="actions-table"
      />
    </div>
  );
}

function ActionForm({ mc, onCancel, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", owner_side: "suntronix", owner_email: "", due_date: "" });
  const save = async () => {
    try {
      await api.post("/governance-reviews/actions", { master_company_id: mc, ...f });
      toast.success("Action logged");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="action-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Title</div>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="action-title" />
        </label>
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <textarea className="input min-h-[70px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="action-description" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Owner side</div>
          <select className="input" value={f.owner_side} onChange={(e) => setF({ ...f, owner_side: e.target.value })} data-testid="action-owner-side">
            <option value="customer">Customer</option><option value="suntronix">Suntronix</option><option value="joint">Joint</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Owner email</div>
          <input className="input" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} data-testid="action-owner-email" />
        </label>
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Due date</div>
          <input type="date" className="input" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="action-due" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="action-save">Log action</button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Decisions */
function DecisionsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/governance-reviews/decisions", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { if (mc) load(); }, [load, mc]);

  return (
    <div className="space-y-4" data-testid="gov-decisions-tab">
      <div className="flex justify-end">
        {isSuntronix && (
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="decision-new">
            <Plus className="h-4 w-4" /> Log decision
          </button>
        )}
      </div>
      {showForm && <DecisionForm mc={mc} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md divide-y" data-testid="decisions-list">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No decisions logged yet.</div>
        ) : items.map((d) => (
          <div key={d.id} className="p-3">
            <div className="text-sm text-slate-900 font-medium">{d.decision}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {d.context || "—"} · Approved by {d.approved_by || "—"}
              {d.effective_date && <> · Effective {d.effective_date}</>}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Logged {new Date(d.created_at).toLocaleString()} by {d.logged_by}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DecisionForm({ mc, onCancel, onCreated }) {
  const [f, setF] = useState({ decision: "", context: "", approved_by: "", effective_date: "" });
  const save = async () => {
    try {
      await api.post("/governance-reviews/decisions", { master_company_id: mc, ...f });
      toast.success("Decision logged");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="decision-form">
      <label className="block">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Decision</div>
        <input className="input" value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })} data-testid="decision-title" />
      </label>
      <label className="block">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Context</div>
        <textarea className="input min-h-[70px]" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} data-testid="decision-context" />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Approved by</div>
          <input className="input" value={f.approved_by} onChange={(e) => setF({ ...f, approved_by: e.target.value })} data-testid="decision-approved" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Effective date</div>
          <input type="date" className="input" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="decision-effective" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="decision-save">Log</button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Shared UI */
function StatusEditor({ kind, item, onChanged, disabled }) {
  const options = kind === "risk"
    ? ["open", "mitigating", "accepted", "closed"]
    : ["open", "in_progress", "completed", "cancelled"];
  const set = async (v) => {
    try {
      await api.patch(`/governance-reviews/${kind}s/${item.id}`, { status: v });
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (disabled) return <span className={statusChip(item.status)}>{item.status?.replace("_", " ")}</span>;
  return (
    <select
      className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
      value={item.status || "open"}
      onChange={(e) => set(e.target.value)}
      data-testid={`${kind}-status-${item.id}`}>
      {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
    </select>
  );
}

function RegistryTable({ columns, rows, renderRow, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
          <tr>{columns.map((c) => <th key={c} className="text-left py-2 px-3">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-6 text-slate-500">Nothing here yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              {renderRow(r).map((cell, i) => <td key={i} className="py-2 px-3">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegistrySection({ title, items, render }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md">
      <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">{title}</div>
      <div className="divide-y">
        {items.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">Nothing to show.</div>
        ) : items.map((it) => (
          <div key={it.id} className="p-3">{render(it)}</div>
        ))}
      </div>
    </section>
  );
}

function SummaryTile({ label, value, suffix, tone }) {
  const bg =
    tone === "danger" ? "bg-rose-50 border-rose-200" :
    tone === "warn"   ? "bg-amber-50 border-amber-200" :
                        "bg-white border-slate-200";
  return (
    <div className={"rounded-md border p-4 " + bg}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-3xl font-heading font-semibold tabular-nums text-slate-900 mt-1">{value ?? "—"}</div>
      {suffix && <div className="text-[10px] text-slate-500 mt-1">{suffix}</div>}
    </div>
  );
}

function SkeletonBlock() {
  return <div className="bg-white border border-slate-200 rounded-md p-8 text-sm text-slate-500 animate-pulse">Loading…</div>;
}

function severityChip(s) {
  const map = {
    critical: "bg-rose-100 text-rose-800 border-rose-200",
    high: "bg-orange-100 text-orange-800 border-orange-200",
    medium: "bg-amber-100 text-amber-800 border-amber-200",
    low: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return "inline-block text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border " + (map[s] || map.low);
}

function statusChip(s) {
  const map = {
    published:   "bg-emerald-100 text-emerald-800 border-emerald-200",
    held:        "bg-slate-100 text-slate-800 border-slate-200",
    in_progress: "bg-sky-100 text-sky-800 border-sky-200",
    scheduled:   "bg-white text-slate-700 border-slate-200",
    completed:   "bg-emerald-100 text-emerald-800 border-emerald-200",
    open:        "bg-white text-slate-700 border-slate-200",
    mitigating:  "bg-amber-100 text-amber-800 border-amber-200",
    closed:      "bg-slate-100 text-slate-500 border-slate-200",
    cancelled:   "bg-slate-100 text-slate-500 border-slate-200",
    accepted:    "bg-slate-100 text-slate-700 border-slate-200",
  };
  return "inline-block text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border " + (map[s] || map.open);
}

function kindChip(k) {
  return "inline-block text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border " +
    (k === "qbr" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200");
}

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
}
